import { flowRight } from 'lodash';
import React from 'react';
import { MutationFn } from 'react-apollo';
import I18n from 'i18n-js';

import Currency from '@reverbdotcom/commons/src/currency';
import { parseAmountCents, formatNumber } from '@reverbdotcom/commons/src/money';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroupWithCheckbox from '@reverbdotcom/commons/src/components/form_group_with_checkbox';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import { createAutoOfferMutation, deleteAutoOfferMutation } from '@reverbdotcom/commons/src/offers/offer_operations';
import { RCTextInput } from '@reverbdotcom/commons/src/rc/forms';

import Location from '../../lib/wrapped_location';
import {
  AutoOfferListingFragment,
  CoreAutoOffersCreateAutoOfferMutation,
  CoreAutoOffersDeleteAutoOfferMutation,
} from '../../schemas/rql';

interface IExternalProps {
  loading: boolean;
  listing: AutoOfferListingFragment.Fragment;
  closeModal: () => void;
}

interface IApolloProps {
  createAutoOffer: MutationFn<
  CoreAutoOffersCreateAutoOfferMutation.Mutation,
  CoreAutoOffersCreateAutoOfferMutation.Variables
  >;
  deleteAutoOffer: MutationFn<
  CoreAutoOffersDeleteAutoOfferMutation.Mutation,
  CoreAutoOffersDeleteAutoOfferMutation.Variables
  >;
}

export type IProps = IExternalProps & IApolloProps;

export interface IState {
  messageToBuyer: string;
  autoOfferIsActive: boolean;
  submitFailedMessage: string;
  percentage: number;
}

const MINIMUM_DISCOUNT_PERCENT = 10;

const emptyFormState = {
  autoOfferIsActive: false,
  messageToBuyer: '',
  submitFailedMessage: '',
  percentage: MINIMUM_DISCOUNT_PERCENT,
};

function formReducer(state, action): IState {
  switch (action.type) {
    case 'prefillFormState':
      return {
        ...state,
        autoOfferIsActive: true,
        messageToBuyer: action.value.listing.autoOffers.message,
        percentage: action.value.listing.autoOffers.percentage,
      };
    case 'toggleOfferActive':
      return { ...state, autoOfferIsActive: !state.autoOfferIsActive };
    case 'setPercentage':
      return { ...state, percentage: parseInt(action.value, 10) };
    case 'setMessageToBuyer':
      return { ...state, messageToBuyer: action.value };
    case 'setSubmitFailedMessage':
      return { ...state, submitFailedMessage: action.value };
    default:
      return { ...state };
  }
}

function discountedPrice(amountCents: number, currency: string, percentDiscount: number): string {
  const discountMultiplier = (100 - percentDiscount) / 100;
  const discountedPriceCents = Math.floor(amountCents * discountMultiplier);
  const parsed = parseAmountCents(discountedPriceCents, currency);

  return formatNumber(Number(parsed.amount));
}

function hasAutoOffer(listing: AutoOfferListingFragment.Fragment) {
  return listing && !!listing.autoOffers.id;
}

function hasDisabledExistingAutoOffer(formState: IState, listing: AutoOfferListingFragment.Fragment) {
  return !formState.autoOfferIsActive && hasAutoOffer(listing);
}

function listingTaxIncluded(listing: AutoOfferListingFragment.Fragment) {
  return !!listing.taxIncluded && !!listing.taxIncludedHint;
}

function userCurrencyMatchesShopCurrency(listing: AutoOfferListingFragment.Fragment) {
  return listing.price.currency === listing.sellerPrice.currency;
}

function listingStartingPrice(listing: AutoOfferListingFragment.Fragment) {
  if (listingTaxIncluded(listing) && userCurrencyMatchesShopCurrency(listing)) {
    return listing.price;
  }

  return listing.sellerPrice;
}

function LoadingState() {
  return (
    <div className="align-center">
      {I18n.t('discovery.offers.autoOffers.createForm.loading')}
    </div>
  );
}

function GeneralInformation() {
  return (
    <div className="offers-form__notices">
      <RCAlertBox type="plain">
        <p className="offers-form__notice offers-form__notice--watch">
          {I18n.t('discovery.offers.autoOffers.createForm.watch')}
        </p>
        <p className="offers-form__notice offers-form__notice--cart">
          {I18n.t('discovery.offers.autoOffers.createForm.abandonCart')}
        </p>
        <SanitizedRender
          className="offers-form__notice offers-form__notice--confirm"
          htmlTag="p"
          html={
            I18n.t(
              'discovery.offers.autoOffers.createForm.initialSendHtml',
              {
                tagOpen: '<span class="style-italic">',
                tagClose: '</span>',
              },
            )
          }
        />
        <p className="offers-form__notice offers-form__notice--min-price">
          {I18n.t('discovery.offers.autoOffers.createForm.minPrice')}
        </p>
      </RCAlertBox>
    </div>
  );
}

function DisclaimersBox({ listing }) {
  return (
    <div className="offers-form__notices">
      <p className="offers-form__notice offers-form__notice--time">
        {I18n.t('discovery.offers.autoOffers.createForm.shippingInfo')}
      </p>
      <RCAlertBox type="warning" small>
        <p className="mb-0">
          {I18n.t('discovery.offers.autoOffers.createForm.saleInfoPercentageBased')}
          {listing.condition.displayName === 'Brand New' ? ' ' + I18n.t('discovery.offers.autoOffers.createForm.bumpFeeCap') : null}
        </p>
      </RCAlertBox>
    </div>
  );
}

function percentageValidationError(formState: IState) {
  if (formState.percentage < 10) {
    return I18n.t('discovery.offers.autoOffers.createForm.minimumPercentageError');
  }

  if (formState.percentage >= 100) {
    return I18n.t('discovery.offers.autoOffers.createForm.maximumPercentageError');
  }
  return null;
}

function calculatedPriceLabel(listing, percentOff, currencySymbol, startingPrice) {
  const i18nParams = {
    percent: percentOff.toString(),
    currencySymbol,
    price: discountedPrice(startingPrice.amountCents, startingPrice.currency, percentOff),
    tagOpen: '<span class="weight-bold">',
    tagClose: '</span>',
  };

  if (userCurrencyMatchesShopCurrency(listing)) {
    return listingTaxIncluded(listing) ? <SanitizedRender
      html={
        I18n.t('discovery.offers.autoOffers.createForm.calculatedPriceWithTaxHint',
          {
            ...i18nParams,
            hint: listing.taxIncludedHint,
          },
        )
      }
    /> : <SanitizedRender
      html={
        I18n.t('discovery.offers.autoOffers.createForm.calculatedPrice', i18nParams)
      }
    />;
  }

  return '';
}

export function AutoOfferForm({ loading, listing, createAutoOffer, deleteAutoOffer, closeModal }: IProps) {
  const [formState, dispatch] = React.useReducer(formReducer, emptyFormState);

  React.useEffect(() => {
    if (!loading && hasAutoOffer(listing)) {
      dispatch({ type: 'prefillFormState', value: { listing } });
    }
  }, [listing, loading]);

  if (loading) { return LoadingState(); }

  async function handleSubmit(event) {
    event.preventDefault();
    dispatch({ type: 'setSubmitFailedMessage', value: '' });

    try {
      if (hasDisabledExistingAutoOffer(formState, listing)) {
        await deleteAutoOffer({
          variables: {
            input: {
              listingId: listing.id,
            },
          },
        });
      } else {
        await createAutoOffer({
          variables: {
            input: {
              listingId: listing.id,
              message: formState.messageToBuyer,
              percentage: formState.percentage,
            },
          },
        });
      }

      closeModal();

      // Reload the page since the indicator UI in the auto offer button comes in as a Rails prop
      // If we don't reload, the indicator will be out of sync with the current state of the auto offer
      Location.reload();
    } catch (err) {
      dispatch({ type: 'setSubmitFailedMessage', value: I18n.t('discovery.offers.autoOffers.createForm.submitFailedMessage') });
    }
  }

  const autoOfferCurrency = Currency.get(listing.sellerPrice.currency);
  const percentageValidationMessage = percentageValidationError(formState);
  const errorDisplay = formState.submitFailedMessage;

  return (
    <>
      <GeneralInformation />
      <form>
        <FormGroupWithCheckbox
          inputName="toggle-auto-offers"
          label={I18n.t('discovery.offers.autoOffers.createForm.enable')}
          updateField={() => dispatch({ type: 'toggleOfferActive' })}
          checked={formState.autoOfferIsActive}
        />
        {formState.autoOfferIsActive && (
          <div className="scaling-mt-2">
            {errorDisplay && (
              <RCAlertBox type="warning" small>
                <p className="mb-0">
                  {errorDisplay}
                </p>
              </RCAlertBox>
            )}

            <div className="d-flex fx-align-center mb-2">
              <div className="width-50 mr-2">
                <RCTextInput
                  label={I18n.t('discovery.offers.autoOffers.createForm.percentageLabel')}
                  id="percentage"
                  name="percentage"
                  value={formState.percentage ? formState.percentage.toString() : ''}
                  onChange={(evt) => dispatch({ type: 'setPercentage', value: evt.target.value })}
                  required
                  errorText={percentageValidationMessage}
                  suffixText={'%'}
                  helpText={I18n.t('discovery.offers.autoOffers.createForm.percentageHelpText')}
                />
              </div>
              <div className="width-50" id="calculatedPriceLabel">
                {!percentageValidationMessage && !!formState.percentage && calculatedPriceLabel(
                  listing,
                  formState.percentage,
                  autoOfferCurrency.symbol,
                  listingStartingPrice(listing),
                )}
              </div>
            </div>

            <FormGroupWithInput
              value={formState.messageToBuyer}
              label={I18n.t('discovery.offers.autoOffers.createForm.message')}
              inputName="messageToBuyer"
              inputType="textarea"
              tagOptional
              updateField={({ messageToBuyer }) =>
                dispatch({ type: 'setMessageToBuyer', value: messageToBuyer })
              }
            />
          </div>
        )}
        <div className="offers-form__actions">
          {hasDisabledExistingAutoOffer(formState, listing) && (
            <div className="mb-2">
              <RCAlertBox type="warning" small>
                <h2 className="weight-bold size-120 mb-half icon-l-exclamation-circle">
                  {I18n.t('discovery.offers.autoOffers.createForm.alertTitle')}
                </h2>
                <p className="mb-0">
                  {I18n.t('discovery.offers.autoOffers.createForm.alertCopy')}
                </p>
              </RCAlertBox>
            </div>
          )}
          {formState.autoOfferIsActive && <DisclaimersBox listing={listing} />}
          <button
            className="offers-form__actions__save"
            type="submit"
            onClick={handleSubmit}
            disabled={!formState.percentage || !!percentageValidationMessage}
          >
            {hasDisabledExistingAutoOffer(formState, listing)
              ? I18n.t('discovery.offers.autoOffers.createForm.save')
              : I18n.t('discovery.offers.autoOffers.createForm.submit')}
          </button>
          <button
            className="reverb-modal__actions__back"
            type="button"
            onClick={closeModal}
          >
            {I18n.t('discovery.offers.autoOffers.createForm.cancel')}
          </button>
        </div>
      </form>
    </>
  );
}

export default flowRight([
  createAutoOfferMutation,
  deleteAutoOfferMutation,
])(AutoOfferForm);
